<template>
  <v-row>
    <v-col cols="12" md="10" offset-md="1" lg="8" offset-lg="2">
      <v-card class="mt-4">
        <v-card-text>
          <v-row>
            <v-col :cols="12" md="6">
              <v-dialog v-if="isEditable" v-model="cancelJuryDialog" width="400">
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on" color="error" outlined small style="float:right;transform:translateY(-4px)">
                    <v-icon left>fal fa-trash-alt</v-icon>
                    Remove Jury
                  </v-btn>
                </template>
                <v-card>
                  <v-card-title>Remove Jury</v-card-title>
                  <v-card-text>Are you sure you want to remove this student and clear this jury slot? This cannot be undone.</v-card-text>
                  <v-card-actions>
                    <v-btn text @click="cancelJury()">Remove Jury</v-btn>
                    <v-spacer />
                    <v-btn text @click="cancelJuryDialog = false">Cancel</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
              <h2 style="margin-bottom:10px">Performer: {{ studentName}}</h2>
              <v-list elevation="1">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Jury Date/Time</v-list-item-subtitle>
                    <v-list-item-title>{{ juryDate }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-avatar>{{ juryTerm }}</v-list-item-avatar>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Instructor</v-list-item-subtitle>
                    <v-list-item-title>{{ instructor }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-subtitle>Instrument</v-list-item-subtitle>
                    <v-list-item-title>{{ instrument }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="accompanist">
                  <v-list-item-content>
                    <v-list-item-subtitle>Accompanist</v-list-item-subtitle>
                    <v-list-item-title>{{ accompanist }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col :cols="12" md="6">
              <h2 style="margin-bottom:10px">Repertoire</h2>
              <v-list elevation="1">
                <v-list-item v-for="({ _id, name, composer }, index) of pieces" :key="'item-' + _id" :style="index > 0 ? 'border-top:1px solid lightgrey' : ''">
                  <v-list-item-content>
                    <v-list-item-title>{{ name }}</v-list-item-title>
                    <v-list-item-subtitle>{{ composer }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action v-if="isEditable">
                    <v-dialog width="400">
                      <template v-slot:activator="{ on }">
                        <v-btn v-on="on" icon>
                          <v-icon small>fal fa-pencil</v-icon>
                        </v-btn>
                      </template>
                      <v-card>
                        <v-card-title>Edit Repertoire</v-card-title>
                        <v-card-text>
                          <v-text-field :value="name" label="Name of Piece" outlined @change="(name) => updatePiece(_id, { name })" />
                          <v-text-field :value="composer" label="Composer" outlined @change="(composer) => updatePiece(_id, { composer })" />
                          <p>Click anywhere outside this dialog box to close</p>
                        </v-card-text>
                      </v-card>
                    </v-dialog>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-title>Evaluations</v-card-title>
        <v-tabs v-model="evalTab">
          <v-tab v-for="(evaluation, index) in evaluations" :key="'tab-' + index">{{ evaluation.evaluator.name }}</v-tab>
          <v-spacer/>
          <v-dialog v-if="isAdmin" v-model="evaluatorDialog" width="300">
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" outlined style="margin:5px 16px 0 10px">
                <v-icon left>fal fa-plus</v-icon>
                Add Eval
              </v-btn>
            </template>
            <v-card>
              <v-card-title>Add Evaluation By</v-card-title>
              <v-card-text>
                <v-text-field v-model="evaluator" label="Evaluator"></v-text-field>
              </v-card-text>
              <v-card-actions>
                <v-btn @click="addEvaluation">Start Evaluation</v-btn>
                <v-spacer />
                <v-btn text @click="evaluatorDialog = false">Cancel</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-tabs>
        <v-tabs-items v-model="evalTab">
          <v-tab-item v-for="({ ratings, comment, editable }, index) in evaluations" :key="'tab-content-' + index">
            <evaluation
              :ratings="ratings"
              :comment="comment"
              :editable="editable"
              @comment="(comment) => updateComment({ index, comment })"
              @updateRating="({ name, value }) => updateRating({ index, name, value })"
              @save="saveEvaluation(index)" />
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </v-col>
  </v-row>
</template>
<script>
import { computed, onMounted, ref } from '@vue/composition-api'
import { stringFormatDate } from '../../../helpers/formatters'

export default {
  components: {
    Evaluation: () => import('@/components/student/music/jury/evaluation')
  },
  setup (props, { root }) {
    const newEval = ref(true)
    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const isAdmin = computed(() => root.$store.getters.hasRole(['Technology Services', 'Music']))
    const isLoaded = ref(false)
    const id = computed(() => root.$route.params.id)
    const studentName = ref('')
    const instructor = ref('')
    const instrument = ref('')
    const accompanist = ref('')
    const juryDate = ref('')
    const juryTerm = ref('')
    const pieces = ref([])
    const evalTab = ref(0)
    const evaluations = ref([])
    const ratings = ref([])
    const isEditable = computed(() => isAdmin.value && isLoaded.value && evaluations.value.length === 0)

    const service = root.$feathers.service('student/music/jury-slot')
    const setupService = root.$feathers.service('student/music/jury-setup')

    async function loadJury () {
      try {
        const data = await service.get(id.value)
        if (data != null) {
          if (isAdmin.value || data.student === user.value.directoryId) {
            const student = await root.$feathers.service('directory/people').get(data.student)
            studentName.value = student.name.first + ' ' + student.name.last
            instructor.value = data.instructor
            instrument.value = data.instrument
            if ('accompanist' in data && data.accompanist != null && data.accompanist !== '') {
              accompanist.value = data.accompanist
            }
            juryDate.value = stringFormatDate(data.start)
            juryTerm.value = (data.term.substring(4, 2) === '05' ? 'Spring ' : 'Fall ') + data.term.substring(0, 4)
            pieces.value = data.pieces
            const { data: setupData } = await setupService.find({ query: { term: data.term } })
            if (setupData.length > 0) {
              const { ratings: r, instruments } = setupData[0]
              const rec = instruments.find(({ name }) => name === instrument.value)
              if (rec && 'ratings' in rec && rec.ratings.length > 0) {
                rec.ratings.forEach((text) => r.push(text))
              }
              ratings.value = r
              loadEvaluations(data.eval)
              isLoaded.value = true
            } else {
              root.$store.dispatch('main/snackbar', { color: 'error', text: 'There was an error loading the setup for this jury slot. Please contact Tech Services for assistance.' })
            }
          } else {
            root.$store.dispatch('main/snackbar', { color: 'error', text: 'You do not have access to the selected jury slot' })
            root.$router.push('/student/music')
          }
        } else {
          root.$store.dispatch('main/snackbar', { color: 'error', text: 'The jury slot you attempted to access does not exist.' })
          if (isAdmin.value) root.$router.push('/student/music/admin')
          else root.$router.push('/student/music')
        }
      } catch (e) {
        console.log(e)
        root.$store.dispatch('main/snackbar', { color: 'error', text: 'The jury slot you attempted to access does not exist.' })
        // if (isAdmin.value) root.$router.push('/student/music/admin')
        // else root.$router.push('/student/music')
      }
    }

    onMounted(async () => {
      loadJury()
    })

    function loadEvaluations (evals) {
      evaluations.value = evals.map((obj) => {
        let editable = false
        if (obj.evaluator.email === user.value.email) editable = true
        return { ...obj, editable }
      })
      if (evals.length > 1) {
        // Add a summary tab
        const summary = {
          comment: [],
          ratings: []
        }
        const hash = {}
        for (let i = 0; i < evals.length; i++) {
          summary.comment.push({
            comment: evals[i].comment,
            name: evals[i].evaluator.name
          })
          for (const { name, value } of evals[i].ratings) {
            if (!(name in hash)) hash[name] = value
            else hash[name] += value
          }
        }
        for (const name in hash) {
          summary.ratings.push({ name, value: hash[name] / evals.length })
        }
        evaluations.value.splice(0, 0, { ...summary, editable: false, evaluator: { name: 'Summary', username: '' } })
      }
    }

    async function saveEvaluation (index) {
      const evaluation = evaluations.value[index]
      if (isAdmin.value && (evaluation.isNew || evaluation.evaluator.email === user.value.email)) {
        // Build the object to be patched
        const patch = { }
        if (evaluation.isNew) {
          delete evaluation.isNew
          patch.$push = { eval: { ...evaluation } }
        } else {
          if (evaluations.value[0].evaluator.name === 'Summary') index--
          patch['eval.' + index] = evaluation
        }
        // Patch the evaluation then update the stored list of evaluations
        try {
          const data = await service.patch(id.value, patch)
          loadEvaluations(data.eval)
          root.$store.dispatch('main/snackbar', { active: true, color: 'success', text: 'Evaluation saved', timeout: 8000 })
        } catch (e) {
          root.$store.dispatch('main/snackbar', { active: true, color: 'error', text: 'Error saving evaluation: ' + e, timeout: 8000 })
        }
      }
    }

    async function updateComment ({ index, comment }) {
      evaluations.value.splice(index, 1, { ...evaluations.value[index], comment })
    }
    async function updateRating ({ index, name, value }) {
      const rec = evaluations.value[index]
      const ratings = rec.ratings
      evaluations.value.splice(index, 1, { ...rec,
        ratings: ratings.map(({ name: n, value: v }) => {
          return { name: n, value: n === name ? value : v }
        })
      })
    }

    const evaluatorDialog = ref(false)
    const evaluator = ref('')
    function addEvaluation () {
      evaluations.value.push({
        isNew: true,
        editable: true,
        ratings: ratings.value.map((name) => { return { name, value: 1 } }),
        comment: '',
        evaluator: {
          name: evaluator.value,
          email: user.value.email
        }
      })
      evalTab.value = evaluations.value.length - 1
      evaluatorDialog.value = false
    }

    const cancelJuryDialog = ref(false)
    async function cancelJury () {
      try {
        await service.patch(id.value, {
          instructor: '',
          instrument: '',
          student: null,
          pieces: []
        })
        root.$store.dispatch('main/snackbar', { color: 'success', text: 'Jury cleared successfully' })
        root.$router.push('/student/music/admin/jury')
      } catch (e) {
        root.$store.dispatch('main/snackbar', { color: 'error', text: 'Error clearing jury: ' + e })
      }
    }

    async function updatePiece (pieceId, { name, composer }) {
      const index = pieces.value.findIndex(({ _id }) => _id === pieceId)
      if (index < 0) return
      const query = { arrayFilters: [{ 'el._id': pieceId }] }
      const patch = {}
      if (name) {
        patch['pieces.$[el].name'] = name
        pieces.value.splice(index, 1, { ...pieces.value[index], name })
      } else if (composer) {
        patch['pieces.$[el].composer'] = composer
        pieces.value.splice(index, 1, { ...pieces.value[index], composer })
      }
      await service.patch(id.value, patch, { query })
    }

    return {
      newEval,
      user,
      id,
      isAdmin,
      studentName,
      instructor,
      instrument,
      accompanist,
      juryDate,
      juryTerm,
      pieces,
      evalTab,
      evaluations,
      ratings,
      isEditable,
      saveEvaluation,
      updateComment,
      updateRating,
      evaluatorDialog,
      evaluator,
      addEvaluation,
      cancelJuryDialog,
      cancelJury,
      updatePiece
    }
  }
}
</script>
